/**
 * @file ContractReviewPanel.tsx
 * @description 案件詳情頁中的「合約評審」入口卡片。
 *
 * 按 case.contractReviewState 渲染狀態 Tag 與動作按鈕：
 *   - pending           發起評審
 *   - in_review         繼續填寫 / 查看 / 批示
 *   - accepted          只查看
 *   - rejected_fixable  發起重評
 *   - rejected_terminal 只查看
 *
 * 下方列出評審歷史（每輪一行）。點任一行進入抽屜。
 */

import { useState } from 'react';
import { Button, Card, Empty, List, Space, Tag } from 'antd';
import { message } from '@/lib/antd-app';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractReviewApi, type ContractReviewDTO } from '@/lib/api-client';
import { userCan } from '@zhcb/shared';
import { useAuthStore } from '@/lib/auth-context';
import { REVIEW_STATUS_META, CONCLUSION_LABEL } from '@/lib/contract-review-labels';
import { ContractReviewDrawer } from './ContractReviewDrawer';

interface Props {
  /** 案件 id（用於 query key 與 API path） */
  caseId: number;
  /** 案件當前的合約評審子狀態（來自 case 詳情 API） */
  contractReviewState:
    | 'pending'
    | 'in_review'
    | 'accepted'
    | 'rejected_fixable'
    | 'rejected_terminal';
}

/**
 * 狀態到顯示樣式的映射。color 走 antd Tag 的 preset。
 */
const STATE_META: Record<Props['contractReviewState'], { color: string; text: string }> = {
  pending: { color: 'default', text: '待發起合約評審' },
  in_review: { color: 'processing', text: '評審進行中' },
  accepted: { color: 'success', text: '已受理' },
  rejected_fixable: { color: 'warning', text: '不受理（可補正）' },
  rejected_terminal: { color: 'error', text: '不受理（結案）' },
};

export function ContractReviewPanel({ caseId, contractReviewState }: Props) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [openId, setOpenId] = useState<number | null>(null);

  const { data: listRes } = useQuery({
    queryKey: ['contract-reviews', caseId],
    queryFn: () => contractReviewApi.list(caseId),
    enabled: !!caseId,
  });
  const list: ContractReviewDTO[] = listRes?.items ?? [];

  const createMut = useMutation({
    mutationFn: () => contractReviewApi.create(caseId),
    onSuccess: (res) => {
      message.success('已發起合約評審');
      void qc.invalidateQueries({ queryKey: ['contract-reviews', caseId] });
      // case 詳情頁的 query key 是 ['case-detail', caseId]（見 dispatch/$caseId.tsx），
      // 必須對齊才能在評審建立後立即刷新 contractReviewState 並聯動按鈕顯示
      void qc.invalidateQueries({ queryKey: ['case-detail', caseId] });
      setOpenId(res.item.id);
    },
    onError: (e: Error) => message.error(e.message || '發起失敗'),
  });

  const meta = STATE_META[contractReviewState];
  // 序47：發起/重評改按能力判斷（動態 RBAC），不再硬編 admin/manager 角色代碼
  const canCreateReview = !!user && userCan(user, 'case.contract_review.create');
  // 最新一輪評審單（list 已按 round 升序，末位即最新）
  const latest = list.length > 0 ? list[list.length - 1] : undefined;

  /** 卡片右上 extra 區的動作按鈕集合 */
  const renderExtraButtons = () => {
    const buttons: React.ReactNode[] = [];
    if (contractReviewState === 'pending' && canCreateReview) {
      buttons.push(
        <Button
          key="start"
          type="primary"
          loading={createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          發起評審
        </Button>,
      );
    }
    if (contractReviewState === 'rejected_fixable' && canCreateReview) {
      buttons.push(
        <Button
          key="restart"
          type="primary"
          loading={createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          發起重評
        </Button>,
      );
    }
    if (contractReviewState === 'in_review' && latest) {
      buttons.push(
        <Button key="continue" onClick={() => setOpenId(latest.id)}>
          {latest.status === 'draft' ? '繼續填寫' : '查看 / 批示'}
        </Button>,
      );
    }
    return buttons;
  };

  return (
    <Card
      title={
        <Space>
          <span>合約評審（ZHSR01-12）</span>
          <Tag color={meta.color}>{meta.text}</Tag>
        </Space>
      }
      extra={<Space>{renderExtraButtons()}</Space>}
    >
      {list.length === 0 ? (
        <Empty description="尚未發起合約評審" />
      ) : (
        <List
          size="small"
          header={<strong>評審歷史</strong>}
          dataSource={[...list].reverse()}
          renderItem={(r) => (
            <List.Item
              actions={[
                <Button key="view" type="link" onClick={() => setOpenId(r.id)}>
                  查看
                </Button>,
              ]}
            >
              <Space wrap>
                <strong>第 {r.round} 輪</strong>
                <Tag color={REVIEW_STATUS_META[r.status].color}>
                  {REVIEW_STATUS_META[r.status].text}
                </Tag>
                {r.conclusion && <Tag color="blue">{CONCLUSION_LABEL[r.conclusion]}</Tag>}
                {/* 序158：合約評審編號套用「驗證註冊碼」（客戶註冊碼 ZH-CAS-xxxx，取自 scopeSnapshot.clientCode），回落評審流水號 */}
                <span>{(r.scopeSnapshot?.clientCode as string | undefined) || r.reviewCode}</span>
                <span style={{ color: '#999' }}>{r.updatedAt.slice(0, 10)}</span>
                {r.rejectionReason && (
                  <span style={{ color: '#cf1322' }}>原因：{r.rejectionReason}</span>
                )}
              </Space>
            </List.Item>
          )}
        />
      )}

      <ContractReviewDrawer
        open={openId !== null}
        review={list.find((r) => r.id === openId) ?? null}
        onClose={() => setOpenId(null)}
      />
    </Card>
  );
}
